import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';

export interface OptionType {
  value: string | number;
  label: string;
}

interface SelectProps {
  className?: string;
  selectClassName?: string;
  caption?: string;
  required?: boolean;
  values?: OptionType[];
  field?: string;
  error?: string;
  value?: string | number;
  placeholder?: string;
  formStyle?: boolean;
  onChange?: (value: string | number) => void;
  onBlur?: () => void;
  onStateChange?: (state: { open: boolean }) => void;
}

export const BaseSelect = (props: SelectProps) => {
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(props.value);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedValue(props.value);
  }, [props.value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setOpen(false);
        props.onBlur?.();
        props.onStateChange?.({ open: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [props.onBlur, props.onStateChange]);

  const handleChange = (value: string | number) => {
    setSelectedValue(value);
    props.onChange?.(String(value));
    setOpen(false);
    props.onStateChange?.({ open: false });
  };

  const handleToggle = () => {
    const newState = !open;
    setOpen(newState);
    props.onStateChange?.({ open: newState });
  };

  const selectedOption = props.values?.find((opt) => opt.value === selectedValue) || { value: '', label: props.placeholder };
  return (
    <div className={clsx(' mb-[22px] relative', props.className)} ref={selectRef}>
      <div className='mb-[6px]'>
        {props.required && <span className='pr-2 text-xl'>*</span>}
        <label className={clsx('text-lg tracking-[-0.1px] leading-[25px]')}>{props.caption}</label>
      </div>
      <div className={clsx('relative z-20')}>
        <div
          className={clsx(
            'h-[38px] cursor-pointer w-full px-[15px] tracking-[0.3px]',
            'border border-[#aaaab2]  flex items-center justify-between',
            'bg-white',
            {
              'border-red-600 shadow-[inset_0_2px_6px_0_rgba(212,65,18,0.39)]': props.error,
            },
            props.selectClassName,
          )}
          onClick={handleToggle}
        >
          <span>{selectedOption.label}</span>
          <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
          </svg>
        </div>
        {open && (
          <div className='absolute top-full left-0 right-0 max-h-[160px] overflow-y-auto bg-[#fcfcfc] border border-[#aaaab2]'>
            {props.values?.map((option, index) => (
              <div
                key={index}
                className={clsx('px-[15px] py-[5px] cursor-pointer hover:bg-gray-100', {
                  'bg-gray-200': option.value === selectedValue,
                })}
                onClick={() => handleChange(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      {props.error && (
        <div
          className={clsx(
            'text-sm left-0 mt-0  top-full text-[#d44112] tracking-[-0.1px] text-left whitespace-pre-line',
            props.formStyle && 'absolute top-0',
          )}
        >
          {props.error}
        </div>
      )}
    </div>
  );
};
